package com.partejar.app.feature.workspace.presentation

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.AlertDialog
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import com.partejar.app.core.ui.components.PartejarAlert
import com.partejar.app.core.ui.components.PartejarAlertTone
import com.partejar.app.core.ui.components.PartejarButton
import com.partejar.app.core.ui.components.PartejarButtonVariant
import com.partejar.app.core.ui.components.PartejarCard
import com.partejar.app.core.ui.components.PartejarSkeletonText
import com.partejar.app.core.ui.components.PartejarTextField
import com.partejar.app.core.ui.components.PartejarTopBar
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import com.partejar.app.feature.workspace.data.SessionBootstrapDto
import com.partejar.app.feature.workspace.data.UserWorkspace
import org.koin.compose.koinInject
import androidx.compose.ui.tooling.preview.Preview
import com.partejar.app.feature.workspace.data.SessionUser
import com.partejar.app.feature.workspace.data.WorkspaceProfile
import com.partejar.app.feature.entitlements.data.CommercialState
import com.partejar.app.feature.entitlements.presentation.EntitlementCoordinator

@Composable
fun WorkspaceSelectionScreen(
    onSelected: () -> Unit,
    onCreate: () -> Unit,
    viewModel: WorkspaceViewModel = koinInject()
) {
    val bootstrap by viewModel.bootstrap.collectAsState()
    WorkspaceListLayout(
        title = WorkspaceStrings.chooseTitle,
        bootstrap = bootstrap,
        currentWorkspaceId = null,
        onSelect = { viewModel.select(it); onSelected() },
        onCreate = onCreate
    )
}

@Composable
fun FirstWorkspaceScreen(
    onCreated: () -> Unit,
    viewModel: WorkspaceViewModel = koinInject(),
    entitlementCoordinator: EntitlementCoordinator = koinInject()
) {
    val personal by entitlementCoordinator.personal.collectAsState()
    LaunchedEffect(Unit) { entitlementCoordinator.refreshPersonal() }
    WorkspaceFormScreen(
        title = WorkspaceStrings.firstTitle,
        description = WorkspaceStrings.firstDescription,
        initialName = "",
        submitLabel = "Criar espaço",
        onSubmit = viewModel::create,
        onActivateFreeAndSubmit = viewModel::activateFreeAndCreate,
        needsFreeActivation = (personal as? CommercialState.Available)?.value?.hasPlan == false,
        onSuccess = onCreated,
        viewModel = viewModel
    )
}

@Composable
fun ManageWorkspacesScreen(
    onBack: () -> Unit,
    onSelected: () -> Unit,
    onCreate: () -> Unit,
    viewModel: WorkspaceViewModel = koinInject()
) {
    val bootstrap by viewModel.bootstrap.collectAsState()
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = WorkspaceStrings.plural,
                leadingContent = { BackAction(onBack) })
        }
    ) { padding ->
        Box(Modifier.padding(padding)) {
            WorkspaceListLayout(
                title = null,
                bootstrap = bootstrap,
                currentWorkspaceId = viewModel.currentWorkspace()?.id,
                onSelect = { viewModel.select(it); onSelected() },
                onCreate = onCreate
            )
        }
    }
}

@Composable
fun CreateWorkspaceScreen(
    onBack: () -> Unit,
    onCreated: () -> Unit,
    viewModel: WorkspaceViewModel = koinInject(),
    entitlementCoordinator: EntitlementCoordinator = koinInject()
) {
    val personal by entitlementCoordinator.personal.collectAsState()
    LaunchedEffect(Unit) { entitlementCoordinator.refreshPersonal() }
    WorkspaceFormScreen(
        title = "Criar espaço",
        initialName = "",
        submitLabel = "Criar espaço",
        onSubmit = viewModel::create,
        onActivateFreeAndSubmit = viewModel::activateFreeAndCreate,
        needsFreeActivation = (personal as? CommercialState.Available)?.value?.hasPlan == false,
        onSuccess = onCreated,
        onBack = onBack,
        viewModel = viewModel
    )
}

@Composable
fun EditWorkspaceScreen(
    workspaceId: Long,
    onBack: () -> Unit,
    onUpdated: () -> Unit,
    viewModel: WorkspaceViewModel = koinInject()
) {
    val workspace = viewModel.bootstrap.value?.workspaces?.firstOrNull { it.id == workspaceId }
    if (workspace == null || !workspace.canEdit) {
        LaunchedEffect(Unit) { onBack() }
        return
    }
    WorkspaceFormScreen(
        title = "Editar espaço",
        initialName = workspace.nome,
        submitLabel = "Salvar alterações",
        onSubmit = { viewModel.update(workspaceId, it) },
        onSuccess = onUpdated,
        onBack = onBack,
        viewModel = viewModel
    )
}

@Composable
private fun WorkspaceFormScreen(
    title: String,
    initialName: String,
    submitLabel: String,
    onSubmit: (String) -> Unit,
    onActivateFreeAndSubmit: ((String) -> Unit)? = null,
    needsFreeActivation: Boolean = false,
    onSuccess: () -> Unit,
    viewModel: WorkspaceViewModel,
    description: String? = null,
    onBack: (() -> Unit)? = null
) {
    var name by remember(initialName) { mutableStateOf(initialName) }
    val formState by viewModel.formState.collectAsState()
    var showFreeConfirmation by remember { mutableStateOf(false) }
    LaunchedEffect(formState) {
        if (formState is WorkspaceFormState.Success) {
            viewModel.clearFormState()
            onSuccess()
        }
    }
    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
        topBar = {
            PartejarTopBar(
                title = title,
                leadingContent = { onBack?.let { BackAction(it) } }
            )
        }
    ) { padding ->
        val spacing = PartejarTheme.spacing
        Column(
            modifier = Modifier.fillMaxSize().padding(padding).padding(spacing.space24),
            verticalArrangement = Arrangement.Center
        ) {
            description?.let {
                Text(it, color = MaterialTheme.colorScheme.onSurfaceVariant)
                Spacer(Modifier.height(spacing.space24))
            }
            PartejarTextField(
                value = name,
                onValueChange = { name = it },
                label = "Nome",
                isError = formState is WorkspaceFormState.Error,
                supportingText = (formState as? WorkspaceFormState.Error)?.message
            )
            PartejarButton(
                text = submitLabel,
                onClick = {
                    if (needsFreeActivation && onActivateFreeAndSubmit != null) showFreeConfirmation = true
                    else onSubmit(name)
                },
                isLoading = formState is WorkspaceFormState.Submitting,
                modifier = Modifier.padding(top = spacing.space20)
            )
        }
    }
    if (showFreeConfirmation) {
        AlertDialog(
            onDismissRequest = { showFreeConfirmation = false },
            title = { Text("Ativar plano Free?") },
            text = { Text("O plano Free será ativado para criar um espaço próprio. Não há cobrança nesta etapa.") },
            confirmButton = {
                TextButton(onClick = {
                    showFreeConfirmation = false
                    onActivateFreeAndSubmit?.invoke(name)
                }) { Text("Ativar e criar") }
            },
            dismissButton = { TextButton(onClick = { showFreeConfirmation = false }) { Text("Agora não") } }
        )
    }
}

@Composable
private fun WorkspaceListLayout(
    title: String?,
    bootstrap: SessionBootstrapDto?,
    currentWorkspaceId: Long?,
    onSelect: (Long) -> Unit,
    onCreate: () -> Unit
) {
    val spacing = PartejarTheme.spacing

    if (bootstrap == null) {
        LoadingContent()
        return
    }

    LazyColumn(
        modifier = Modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background),
        contentPadding = PaddingValues(spacing.space24),
        verticalArrangement = Arrangement.spacedBy(spacing.space12)
    ) {
        title?.let { heading ->
            item {
                Text(
                    text = heading,
                    modifier = Modifier.semantics { heading() },
                    style = MaterialTheme.typography.headlineSmall
                )
            }
        }

        if (bootstrap.workspaces.isEmpty()) {
            item {
                PartejarAlert(
                    title = "Você ainda não possui espaços",
                    tone = PartejarAlertTone.INFO
                ) {
                    Text(WorkspaceStrings.firstDescription)
                }
            }
        }

        items(bootstrap.workspaces, key = { it.id }) { workspace ->
            WorkspaceCard(
                workspace = workspace,
                selected = currentWorkspaceId == workspace.id,
                onSelect = onSelect
            )
        }

        item {
            PartejarButton(
                text = "Criar espaço",
                onClick = onCreate,
                variant = PartejarButtonVariant.SECONDARY
            )
        }
    }
}

@Composable
private fun WorkspaceCard(
    workspace: UserWorkspace,
    selected: Boolean,
    onSelect: (Long) -> Unit
) {
    val spacing = PartejarTheme.spacing
    PartejarCard(onClick = { onSelect(workspace.id) }, modifier = Modifier.fillMaxWidth()) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                PartejarIcons.Navigation.workspace,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary
            )
            Column(Modifier.weight(1f).padding(horizontal = spacing.space12)) {
                Text(workspace.nome, style = MaterialTheme.typography.titleMedium)
                Text(workspace.perfil.nome, color = MaterialTheme.colorScheme.onSurfaceVariant)
                if (selected) Text(
                    "Espaço atual",
                    color = MaterialTheme.colorScheme.primary,
                    style = MaterialTheme.typography.labelMedium
                )
            }
        }
    }
}

@Composable
private fun BackAction(onBack: () -> Unit) {
    androidx.compose.material3.IconButton(onClick = onBack) {
        Icon(PartejarIcons.Actions.back, contentDescription = "Voltar")
    }
}

@Composable
private fun LoadingContent(modifier: Modifier = Modifier) {
    LazyColumn(
        modifier = modifier
            .fillMaxSize()
            .background(MaterialTheme.colorScheme.background),
        contentPadding = PaddingValues(PartejarTheme.spacing.space16),
        verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space12)
    ) {
        items(3) {
            PartejarCard(modifier = Modifier.fillMaxWidth()) {
                Column(verticalArrangement = Arrangement.spacedBy(PartejarTheme.spacing.space8)) {
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.62f).height(PartejarTheme.spacing.space16))
                    PartejarSkeletonText(Modifier.fillMaxWidth(0.42f).height(PartejarTheme.spacing.space12))
                }
            }
        }
    }
}

private val previewSession = SessionBootstrapDto(
    usuario = SessionUser(1, "Ana Souza", "ana@partejar.local"),
    workspaces = listOf(
        UserWorkspace(
            1,
            "Clínica Aurora",
            WorkspaceProfile("ADMINISTRADOR", "Administrador"),
            "OUTRO",
            usuarioAtualEhProprietario = true
        ),
        UserWorkspace(
            7,
            "Equipe Nascer",
            WorkspaceProfile("EQUIPE_CLINICA", "Equipe clínica"),
            "ENFERMEIRA_OBSTETRICA"
        )
    )
)

@Preview(name = "Seleção · múltiplos", widthDp = 390, heightDp = 844)
@Composable
private fun WorkspaceSelectionPreview() = PartejarTheme {
    WorkspaceListLayout(
        title = WorkspaceStrings.chooseTitle,
        bootstrap = previewSession,
        currentWorkspaceId = null,
        onSelect = {},
        onCreate = {}
    )
}

@Preview(name = "Meus espaços · um", widthDp = 390, heightDp = 844)
@Composable
private fun SingleWorkspacePreview() = PartejarTheme {
    WorkspaceListLayout(
        title = WorkspaceStrings.plural,
        bootstrap = previewSession.copy(workspaces = previewSession.workspaces.take(1)),
        currentWorkspaceId = 1,
        onSelect = {},
        onCreate = {}
    )
}

@Preview(name = "Meus espaços · vazio", widthDp = 390, heightDp = 844)
@Composable
private fun EmptyWorkspacesPreview() = PartejarTheme {
    WorkspaceListLayout(
        title = WorkspaceStrings.plural,
        bootstrap = previewSession.copy(workspaces = emptyList()),
        currentWorkspaceId = null,
        onSelect = {},
        onCreate = {}
    )
}

@Preview(name = "Criar espaço", widthDp = 390, heightDp = 844)
@Composable
private fun CreateWorkspacePreview() = PartejarTheme {
    Column(
        Modifier.fillMaxSize().padding(PartejarTheme.spacing.space24),
        verticalArrangement = Arrangement.Center
    ) {
        Text("Criar espaço", style = MaterialTheme.typography.headlineSmall)
        PartejarTextField(
            "",
            {},
            "Nome",
            modifier = Modifier.padding(top = PartejarTheme.spacing.space24)
        )
        PartejarButton(
            "Criar espaço",
            {},
            modifier = Modifier.padding(top = PartejarTheme.spacing.space20)
        )
    }
}

@Preview(name = "Editar espaço", widthDp = 390, heightDp = 844)
@Composable
private fun EditWorkspacePreview() = PartejarTheme {
    Column(
        Modifier.fillMaxSize().padding(PartejarTheme.spacing.space24),
        verticalArrangement = Arrangement.Center
    ) {
        Text("Editar espaço", style = MaterialTheme.typography.headlineSmall)
        PartejarTextField(
            "Clínica Aurora",
            {},
            "Nome",
            modifier = Modifier.padding(top = PartejarTheme.spacing.space24)
        )
        PartejarButton(
            "Salvar alterações",
            {},
            modifier = Modifier.padding(top = PartejarTheme.spacing.space20)
        )
    }
}
